/**
 * The indexer's HTTP/WebSocket API, decoded into domain types.
 *
 * The shapes come straight from the Rust indexer: thoughts carry the exact list
 * of neurons the kernel fired, burns carry who destroyed how much OBRAIN.
 */

import {
  asArray,
  asBoolean,
  asNumber,
  optionalNumber,
  asString,
  DecodeError,
  field,
  fetchJson,
  optionalField,
  parseJson,
  type JsonValue,
} from "./json";

export type BrainInfo = {
  readonly address: string;
  readonly owner: string;
  readonly isSealed: boolean;
  readonly neurons: number;
  readonly words: number;
  readonly segments: number;
  readonly sensory: number;
  readonly threshold: number;
  /** how many tapes the brain was sealed with; null when the node would not say */
  readonly tapes: number | null;
  readonly stateRoot: string;
  readonly tick: number;
  readonly headBlock: number;
};

/**
 * One thought's own new firings: the tick it came from, and the neurons it fired
 * that no earlier thought in the window had already claimed.
 */
export type Firing = {
  readonly tick: Tick;
  readonly neurons: readonly number[];
};

/**
 * Whether this row is one of the brain's own thoughts, not an activity row.
 *
 * The indexer publishes one generated row per block on the same channel and in
 * the same shape, so the stage keeps moving between thoughts - see
 * `publish_activity` in indexer/src/main.rs. Those rows carry no transaction and
 * no state root, and their `tick` field is the *block number*: anything that
 * reports the brain's own state has to ask this first.
 */
export function isThought(tick: Tick): boolean {
  return tick.stateRoot !== "" && tick.txHash !== "";
}

/**
 * The hatchable firings in a served tick window, newest thought first.
 *
 * The window mixes the brain's own thoughts with the indexer's per-block activity
 * rows. Only a thought names a specimen - an activity row carries no transaction
 * and no state root, so no genome is derivable from it - and a neuron is one
 * specimen, ever, so a thought that fires an already-shown neuron adds nothing.
 *
 * The walk starts at the newest thought, so the first rows are the newest
 * firings on the chain, never the oldest ones the window happens to still hold.
 * Every neuron the window fired is offered unless a caller sets a cap: the table
 * that reads this scrolls, so the window - not a chosen number - is the limit.
 */
export function newestFirings(
  ticks: readonly Tick[],
  cap: number = Number.POSITIVE_INFINITY,
): Firing[] {
  const seen = new Set<number>();
  const firings: Firing[] = [];

  for (let at = ticks.length - 1; at >= 0 && seen.size < cap; at -= 1) {
    const tick = ticks[at];

    if (!isThought(tick)) continue;

    const neurons = tick.fired.filter((neuron) => !seen.has(neuron)).slice(0, cap - seen.size);

    for (const neuron of neurons) seen.add(neuron);

    if (neurons.length > 0) firings.push({ tick, neurons });
  }

  return firings;
}

/** How many neurons the window's own thoughts fired: what tonight's count says. */
export function firingCount(ticks: readonly Tick[]): number {
  return ticks.reduce((total, tick) => (isThought(tick) ? total + tick.fired.length : total), 0);
}

/** One thought: what it was fed, what it did, and the neurons that fired. */
export type Tick = {
  readonly tick: number;
  readonly block: number;
  readonly timestamp: number;
  readonly txHash: string;
  readonly input: string;
  readonly readout: readonly number[];
  readonly synapses: number;
  readonly spiked: number;
  readonly capped: boolean;
  readonly stateRoot: string;
  readonly fired: readonly number[];
};

/** One burn: OBRAIN destroyed to feed one channel at one tier. */
export type Feed = {
  readonly tick: number;
  readonly block: number;
  readonly timestamp: number;
  readonly txHash: string;
  readonly feeder: string;
  readonly channel: number;
  readonly tier: number;
  readonly amount: string;
};

export type FeedState = {
  readonly token: string;
  readonly dead: string;
  readonly burned: string;
  readonly feeds: number;
  readonly tick: number;
  readonly tierAmounts: readonly [string, string, string];
};

export type ChainInfo = {
  readonly chainId: number;
  readonly explorer: string;
  readonly address: string;
};

/**
 * One Arc block. Arc mines roughly twice a second, so this is not a curiosity:
 * it is the clock the tissue blinks on, and the hash is what decides which
 * neurons answer.
 */
export type Block = {
  readonly number: number;
  readonly hash: string;
  readonly txs: number;
  readonly timestamp: number;
};

export type History = ChainInfo & {
  readonly brain: BrainInfo | null;
  readonly block: Block | null;
  readonly ticks: readonly Tick[];
  readonly feeds: readonly Feed[];
};

function decodeBrain(source: JsonValue): BrainInfo {
  return {
    address: asString(field(source, "address", "brain"), "brain.address"),
    owner: asString(field(source, "owner", "brain"), "brain.owner"),
    isSealed: asBoolean(field(source, "is_sealed", "brain"), "brain.is_sealed"),
    neurons: asNumber(field(source, "n_neurons", "brain"), "brain.n_neurons"),
    words: asNumber(field(source, "n_words", "brain"), "brain.n_words"),
    segments: asNumber(field(source, "segments", "brain"), "brain.segments"),
    sensory: asNumber(field(source, "sensory", "brain"), "brain.sensory"),
    threshold: asNumber(field(source, "threshold", "brain"), "brain.threshold"),
    tapes: optionalNumber(field(source, "tapes", "brain"), "brain.tapes"),
    stateRoot: asString(field(source, "state_root", "brain"), "brain.state_root"),
    tick: asNumber(field(source, "tick", "brain"), "brain.tick"),
    headBlock: asNumber(field(source, "head_block", "brain"), "brain.head_block"),
  };
}

function decodeTick(source: JsonValue): Tick {
  const fired = asArray(field(source, "fired", "tick"), "tick.fired").map((id, index) =>
    asNumber(id, `tick.fired[${index}]`),
  );

  return {
    tick: asNumber(field(source, "tick", "tick"), "tick.tick"),
    block: asNumber(field(source, "block", "tick"), "tick.block"),
    timestamp: asNumber(optionalField(source, "timestamp", "tick") ?? 0, "tick.timestamp"),
    txHash: asString(field(source, "tx_hash", "tick"), "tick.tx_hash"),
    input: asString(field(source, "input_agg", "tick"), "tick.input_agg"),
    readout: asArray(field(source, "readout", "tick"), "tick.readout").map((value, index) =>
      asNumber(value, `tick.readout[${index}]`),
    ),
    synapses: asNumber(field(source, "synapses", "tick"), "tick.synapses"),
    spiked: asNumber(field(source, "spiked", "tick"), "tick.spiked"),
    capped: asBoolean(field(source, "capped", "tick"), "tick.capped"),
    stateRoot: asString(field(source, "state_root", "tick"), "tick.state_root"),
    fired,
  };
}

function decodeFeed(source: JsonValue): Feed {
  return {
    tick: asNumber(field(source, "round_tick", "feed"), "feed.round_tick"),
    block: asNumber(field(source, "block", "feed"), "feed.block"),
    timestamp: asNumber(optionalField(source, "timestamp", "feed") ?? 0, "feed.timestamp"),
    txHash: asString(field(source, "tx_hash", "feed"), "feed.tx_hash"),
    feeder: asString(field(source, "feeder", "feed"), "feed.feeder"),
    channel: asNumber(field(source, "channel", "feed"), "feed.channel"),
    tier: asNumber(field(source, "tier", "feed"), "feed.tier"),
    amount: asString(field(source, "amount", "feed"), "feed.amount"),
  };
}

function decodeFeedState(source: JsonValue): FeedState {
  const tiers = asArray(field(source, "tier_amounts", "state"), "state.tier_amounts");

  return {
    token: asString(field(source, "token", "state"), "state.token"),
    dead: asString(field(source, "dead", "state"), "state.dead"),
    burned: asString(field(source, "burned", "state"), "state.burned"),
    feeds: asNumber(field(source, "feeds", "state"), "state.feeds"),
    tick: asNumber(field(source, "tick", "state"), "state.tick"),
    tierAmounts: [
      asString(tiers[0], "state.tier_amounts[0]"),
      asString(tiers[1], "state.tier_amounts[1]"),
      asString(tiers[2], "state.tier_amounts[2]"),
    ] as const,
  };
}

function decodeChain(source: JsonValue): ChainInfo {
  return {
    chainId: asNumber(field(source, "chain_id", "chain"), "chain.chain_id"),
    explorer: asString(field(source, "explorer", "chain"), "chain.explorer"),
    address: asString(field(source, "address", "chain"), "chain.address"),
  };
}

function decodeBlock(source: JsonValue): Block {
  return {
    number: asNumber(field(source, "number", "block"), "block.number"),
    hash: asString(field(source, "hash", "block"), "block.hash"),
    txs: asNumber(field(source, "txs", "block"), "block.txs"),
    timestamp: asNumber(field(source, "timestamp", "block"), "block.timestamp"),
  };
}

function optionalBlock(source: JsonValue, label: string): Block | null {
  const block = optionalField(source, "block", label);

  return block === null ? null : decodeBlock(block);
}

/**
 * The header the site opens with: the feeding game as the contract sees it, plus
 * the block the clock is on. One request, and the viewer has a live block number
 * before the stream has even connected.
 */
/** One tape as the brain's index describes it: no payload, just the facts. */
export type TapeRow = {
  readonly index: number;
  readonly address: string;
  readonly startNeuron: number;
  readonly bytes: number;
  readonly sha256: string;
};

/** The 85 tapes: index, address, first neuron, size, hash. */
export async function fetchTapes(): Promise<{ brain: string; tapes: readonly TapeRow[] }> {
  const source = await fetchJson("/api/tapes");

  return {
    brain: asString(field(source, "brain", "tapes"), "tapes.brain"),
    tapes: asArray(field(source, "tapes", "tapes"), "tapes").map((row) => ({
      index: asNumber(field(row, "index", "tape"), "tape.index"),
      address: asString(field(row, "address", "tape"), "tape.address"),
      startNeuron: asNumber(field(row, "startNeuron", "tape"), "tape.startNeuron"),
      bytes: asNumber(field(row, "bytes", "tape"), "tape.bytes"),
      sha256: asString(field(row, "sha256", "tape"), "tape.sha256"),
    })),
  };
}

export async function fetchHeader(): Promise<{
  state: FeedState;
  block: Block | null;
  brain: BrainInfo | null;
}> {
  const source = await fetchJson("/api/state");
  const brainSource = optionalField(source, "brain", "state");

  return {
    state: decodeFeedState(field(source, "feed_state", "state")),
    block: optionalBlock(source, "state"),
    // the header already carries the brain's own report: the vitals rail reads
    // its wiring and seal from here instead of paying for a second request
    brain: brainSource === null ? null : decodeBrain(brainSource),
  };
}

/**
 * The newest row the served window holds that is a thought, not an activity row.
 *
 * Same rule as newestFirings: a row without a transaction and a state root is
 * the indexer's own per-block heartbeat, and the rail must not report a
 * heartbeat as a thought the brain had.
 */
export function newestThought(ticks: readonly Tick[]): Tick | null {
  for (let at = ticks.length - 1; at >= 0; at -= 1) {
    if (isThought(ticks[at])) return ticks[at];
  }

  return null;
}

export async function fetchHistory(): Promise<History> {
  const brainSource = optionalField(await fetchJson("/api/brain"), "brain", "brain");
  const ticksSource = await fetchJson("/api/ticks");
  const feedsSource = await fetchJson("/api/feeds");
  // /api/state carries the chain header but dies with the RPC; /api/brain
  // serves the same fields and keeps the view up in degraded mode
  const chainSource = await fetchJson("/api/state").catch(() => fetchJson("/api/brain"));

  return {
    ...decodeChain(chainSource),
    brain: brainSource === null ? null : decodeBrain(brainSource),
    block: optionalBlock(chainSource, "state"),
    ticks: asArray(field(ticksSource, "ticks", "ticks"), "ticks").map(decodeTick),
    feeds: asArray(field(feedsSource, "feeds", "feeds"), "feeds").map(decodeFeed),
  };
}

/**
 * The live potential field: one little-endian i16 per neuron.
 *
 * The tick is part of the URL on purpose. The field is the same bytes for every
 * viewer until the brain thinks again, and both Cloudflare and the browser are
 * allowed to keep it - but only for the tick it belongs to. A bare path would be
 * cached once and then served long after a thought moved the field, which is the
 * one thing this view must never do: show a picture the chain has moved on from.
 *
 * ponytail: no cache-busting for old ticks - a URL per tick is what makes the
 * edge hold the heavy payload for everyone watching the same thought.
 */
export async function fetchPotentials(tick: number): Promise<Int16Array> {
  const response = await fetch(`/api/pot.bin?tick=${tick}`);

  if (!response.ok) throw new DecodeError(`pot.bin: HTTP ${response.status}`);

  return new Int16Array(await response.arrayBuffer());
}

export type StreamHandlers = {
  readonly onHistory: (history: History) => void;
  readonly onTick: (tick: Tick) => void;
  readonly onFeed: (feed: Feed) => void;
  readonly onBlock: (block: Block) => void;
  readonly onStatus: (status: string) => void;
};

export type Stream = { readonly close: () => void };

export function openStream(handlers: StreamHandlers): Stream {
  const scheme = location.protocol === "https:" ? "wss" : "ws";
  let socket: WebSocket | null = null;
  let closed = false;

  const connect = (): void => {
    if (closed) return;
    socket = new WebSocket(`${scheme}://${location.host}/ws`);
    socket.addEventListener("open", () => handlers.onStatus("live"));
    socket.addEventListener("message", (event) => {
      // the indexer only ever sends text frames
      if (event.data instanceof Blob || event.data instanceof ArrayBuffer) return;

      try {
        const message = parseJson(event.data, "ws");
        const type = asString(field(message, "type", "ws"), "ws.type");

        if (type === "history") {
          const block = optionalBlock(message, "ws");

          handlers.onHistory({
            ...decodeChain(message),
            block,
            brain:
              optionalField(message, "brain", "ws") === null
                ? null
                : decodeBrain(field(message, "brain", "ws")),
            ticks: asArray(field(message, "ticks", "ws"), "ws.ticks").map(decodeTick),
            feeds: asArray(field(message, "feeds", "ws"), "ws.feeds").map(decodeFeed),
          });

          return;
        }

        if (type === "block") {
          handlers.onBlock(decodeBlock(field(message, "block", "ws")));

          return;
        }

        if (type === "tick") {
          handlers.onTick(decodeTick(field(message, "event", "ws")));

          return;
        }

        if (type === "feed") handlers.onFeed(decodeFeed(field(message, "event", "ws")));
      } catch (error) {
        handlers.onStatus(error instanceof Error ? error.message : "bad message");
      }
    });
    socket.addEventListener("close", () => {
      if (closed) return;
      handlers.onStatus("reconnecting");
      window.setTimeout(connect, 1_500);
    });
    socket.addEventListener("error", () => socket?.close());
  };

  connect();

  return {
    close: () => {
      closed = true;
      socket?.close();
    },
  };
}
